/**
 * Sprint 8A · Visual item grouping helper (Presentation-only).
 * ------------------------------------------------------------
 * Groups a flat items[] array by (product identity + decoration + unit price)
 * so read-only document views can render a single product header with its
 * size/color variants nested underneath.
 *
 * Contract:
 *   • Pure function. Never mutates the input array.
 *   • Never persists anything. Consumers must keep sending the flat items[] to the backend.
 *   • When a group ends up with a single variant, the caller MAY render it
 *     using its legacy flat layout (backward-visual-compatibility).
 *
 * Returns: Array<{
 *   key: string,
 *   header: { catalog_image_url, description, product_code, unit_price,
 *             commercial_group, commercial_tier },
 *   variants: Array<Item & { __originalIndex: number }>,
 *   totalQty: number,
 *   totalAmount: number,
 * }>
 */
export function groupItemsByProduct(items) {
  const groups = [];
  const indexByKey = new Map();
  (items || []).forEach((it, i) => {
    // Prefer `description` as product identity: `catalog_sku` and `product_code`
    // typically bake in color/size variants (e.g. "A4N2306-Black-L") which would
    // prevent legitimate grouping. `description` reflects the parent product name.
    const identity =
      it.description ||
      it.product_code ||
      it.catalog_sku ||
      `__unnamed_${i}`;
    const key = [
      identity,
      it.decoration_method || it.decoration_method_id || "",
      it.decoration_position || it.logo_location_id || "",
      Number(it.unit_price || 0).toFixed(4),
    ].join("::");
    let g = indexByKey.get(key);
    if (!g) {
      g = {
        key,
        header: {
          catalog_image_url: it.catalog_image_url || "",
          description: it.description || it.product_code || "",
          product_code: it.product_code || it.catalog_sku || "",
          unit_price: Number(it.unit_price || 0),
          commercial_group: it.commercial_group || "",
          commercial_tier: it.commercial_tier || "",
        },
        variants: [],
        totalQty: 0,
        totalAmount: 0,
      };
      indexByKey.set(key, g);
      groups.push(g);
    }
    g.variants.push({ ...it, __originalIndex: i });
    g.totalQty += Number(it.quantity || 0);
    const lineAmt =
      it.subtotal != null
        ? Number(it.subtotal)
        : Number(it.quantity || 0) * Number(it.unit_price || 0);
    g.totalAmount += lineAmt;
  });
  return groups;
}
